import { FormEvent, useState } from 'react';
import { Check, Gift, Hand, Scissors, Sparkles, Store } from 'lucide-react';
import { useCreateAccountContext } from './Context';

type TipoEstabelecimento = 'barbearia' | 'salao de beleza' | 'manicure' | 'outro';

const TIPOS: { value: TipoEstabelecimento; label: string; Icon: typeof Store }[] = [
    { value: 'barbearia', label: 'Barbearia', Icon: Scissors },
    { value: 'salao de beleza', label: 'Salão de beleza', Icon: Sparkles },
    { value: 'manicure', label: 'Manicure', Icon: Hand },
    { value: 'outro', label: 'Outro', Icon: Store },
];

export const CreateAccountEstab = () => {
    const { loading, handleSubmitAccountEstab } = useCreateAccountContext();
    const [form, setForm] = useState({
        estabName: '',
        userName: '',
        userEmail: '',
        tipo: '' as '' | TipoEstabelecimento,
    });

    const onChange = (field: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!form.tipo) return;

        handleSubmitAccountEstab({
            estabName: form.estabName.trim(),
            userName: form.userName.trim(),
            userEmail: form.userEmail.trim(),
            tipo: form.tipo,
        });
    };

    return (
        <form className="space-y-5" onSubmit={onSubmit}>
            <label className="form-control w-full">
                <span className="mb-1.5 text-sm font-medium">Nome do estabelecimento</span>
                <input
                    type="text"
                    required
                    disabled={loading}
                    value={form.estabName}
                    onChange={(e) => onChange('estabName', e.target.value)}
                    className="input input-bordered h-12 w-full rounded-xl"
                    placeholder="Ex.: Studio Bella"
                    autoComplete="organization"
                />
            </label>

            <fieldset className="space-y-2" disabled={loading}>
                <legend className="mb-1.5 text-sm font-medium">Tipo de negócio</legend>
                <div role="radiogroup" className="grid grid-cols-2 gap-2">
                    {TIPOS.map(({ value, label, Icon }) => {
                        const selected = form.tipo === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => onChange('tipo', value)}
                                className={`relative flex h-[4.5rem] flex-col items-start justify-between rounded-xl border px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                                    selected
                                        ? 'border-primary bg-primary/[0.06] font-medium text-base-content'
                                        : 'border-base-content/10 bg-base-100 text-base-content/80 hover:border-base-content/20'
                                }`}
                            >
                                <Icon size={18} strokeWidth={1.75} className={selected ? 'text-primary' : 'text-base-content/45'} />
                                <span>{label}</span>
                                {selected && (
                                    <span className="absolute right-2.5 top-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-content">
                                        <Check size={11} strokeWidth={3} />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </fieldset>

            <label className="form-control w-full">
                <span className="mb-1.5 text-sm font-medium">Seu nome</span>
                <input
                    type="text"
                    required
                    disabled={loading}
                    value={form.userName}
                    onChange={(e) => onChange('userName', e.target.value)}
                    className="input input-bordered h-12 w-full rounded-xl"
                    placeholder="Responsável pela conta"
                    autoComplete="name"
                />
            </label>

            <label className="form-control w-full">
                <span className="mb-1.5 text-sm font-medium">E-mail</span>
                <input
                    type="email"
                    required
                    disabled={loading}
                    value={form.userEmail}
                    onChange={(e) => onChange('userEmail', e.target.value)}
                    className="input input-bordered h-12 w-full rounded-xl"
                    placeholder="nome@empresa.com"
                    autoComplete="email"
                    inputMode="email"
                />
                <span className="mt-1.5 text-xs text-base-content/50">
                    Você vai receber um link para criar sua senha.
                </span>
            </label>

            <div className="flex items-start gap-3 rounded-xl bg-base-100 px-3.5 py-3 ring-1 ring-base-content/[0.06]">
                <Gift size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-primary" />
                <div>
                    <div className="text-sm font-medium">14 dias grátis</div>
                    <div className="mt-0.5 text-xs text-base-content/60">
                        Serviços e horários você configura depois do primeiro acesso.
                    </div>
                </div>
            </div>

            <button
                type="submit"
                disabled={loading || !form.tipo}
                className="btn btn-primary h-12 w-full rounded-xl"
            >
                {loading ? <span className="loading loading-spinner loading-sm" /> : 'Criar conta'}
            </button>
            {!form.tipo && (
                <p className="-mt-2 text-center text-xs text-base-content/50">Selecione o tipo de negócio para continuar.</p>
            )}
        </form>
    );
};
